import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { verificationApi, type VerificationTemplate } from '../api/verificationApi';
import { ivKeys } from '../queryKeys';
import { useIvPracticeId } from './useIvPracticeId';

export function useTemplates() {
  const practiceId = useIvPracticeId();
  const query = useQuery({
    queryKey: ivKeys.templates(practiceId),
    queryFn: () => verificationApi.listTemplates(),
    enabled: !!practiceId,
  });

  return { ...query, templates: query.data ?? [] };
}

interface UpdateRequirementInput {
  templateId: string;
  requirementId: string;
  required: boolean;
}

export function useUpdateRequirement() {
  const queryClient = useQueryClient();
  const practiceId = useIvPracticeId();

  return useMutation({
    mutationFn: ({ templateId, requirementId, required }: UpdateRequirementInput) =>
      verificationApi.updateRequirement(templateId, requirementId, required),
    // Flip the toggle immediately; the server response (or a refetch on error) settles it.
    onMutate: async ({ templateId, requirementId, required }) => {
      await queryClient.cancelQueries({ queryKey: ivKeys.templates(practiceId) });
      const previous = queryClient.getQueryData<VerificationTemplate[]>(ivKeys.templates(practiceId));
      if (previous) {
        queryClient.setQueryData<VerificationTemplate[]>(
          ivKeys.templates(practiceId),
          previous.map((t) =>
            t.id !== templateId
              ? t
              : {
                  ...t,
                  requirements: t.requirements.map((r) =>
                    r.id === requirementId ? { ...r, required } : r,
                  ),
                },
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) queryClient.setQueryData(ivKeys.templates(practiceId), context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ivKeys.templates(practiceId) });
    },
  });
}
